import { useSWRConfig } from 'swr';
import Dialog from '@comp/Dialog/Dialog';
import {
  BACKEND_API_ERROR,
  programmeTypes,
  savedObjectTypes
} from '@constants/index';
import styled from '@emotion/styled';
import Link from '@icon/Link';
import Save from '@icon/Save';
import Saved from '@icon/Saved';
import { classDidNotHappen, isLessonLive, isUpcoming } from '@models/Item';
import { Spacings } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import { useState, forwardRef } from 'react';
import { useSnackbar } from '@stores/snackbar';
import { emptyObject } from '@constants/empty';
import { NEOLESSON } from '@utils/constants';
import useTopology from '@api/hooks/topology/useTopology';
import { save, unsave } from '@api/fetchers/save';
import PDFDownload, { MenuItem, MenuItemLabel } from './PDFDownload';

const Divider = styled.div`
  height: 1px;
  background-color: var(--color-divider);
  width: ${`calc(100% - ${Spacings.SPACING_20})`};
  margin: ${Spacings.SPACING_1B} auto;
`;

const LinkMenuItem = ({ onClick, label, showDivider }) => (
  <>
    <MenuItem onClick={onClick}>
      <Link size="20" color="var(--color-text-secondary)" />
      <MenuItemLabel>{label}</MenuItemLabel>
    </MenuItem>
    {showDivider && <Divider $marginBottom={Spacings.SPACING_6B} />}
  </>
);

const getGotoMenuLabel = (itemInfo) => {
  const { programme_type: programmeType, is_special: isSpecial } =
    itemInfo.programme;
  const isCourse =
    programmeType === programmeTypes.COURSE ||
    programmeType === programmeTypes.HYBRID_COURSE ||
    programmeType === programmeTypes.ONLINE_WITH_EXTERNAL_STREAM ||
    programmeType === programmeTypes.HYBRID_NON_OFFLINE;
  if (isCourse && isSpecial) {
    return 'See special class';
  }
  return `Go to ${isCourse ? 'course page' : 'test series'}`;
};

const PlannerMenuContent = forwardRef(
  (
    {
      goalUID,
      itemInfo,
      closeActionMenu,
      isLesson,
      author,
      endpoint,
      isTest,
      setClickAwayActive,
      isOfflineCourseItem
    },
    ref
  ) => {
    const { slides, isSpecial } = itemInfo;
    const [showUnsaveDialog, setShowUnsavedDialog] = useState(false);
    const { open: showSnackbar } = useSnackbar();

    const onCloseModal = () => {
      setShowUnsavedDialog(false);
      closeActionMenu();
    };

    const { isPlatformGoal } = useTopology(goalUID) || emptyObject;

    const isInCourse = [
      programmeTypes.COURSE,
      programmeTypes.PLATFORM_COURSE,
      programmeTypes.PLATINUM_COURSE,
      programmeTypes.HYBRID_COURSE,
      programmeTypes.ONLINE_WITH_EXTERNAL_STREAM,
      programmeTypes.HYBRID_NON_OFFLINE
    ].includes(itemInfo.programme?.programme_type);

    const isInTestSeries = [
      programmeTypes.TESTSERIES,
      programmeTypes.PLATFORM_TEST_SERIES,
      programmeTypes.PLATINUM_TEST_SERIES,
      programmeTypes.CBT_TEST_SERIES
    ].includes(itemInfo.programme?.programme_type);

    const enableBookmark =
      !isSpecial &&
      !isUpcoming(itemInfo) &&
      !classDidNotHappen(itemInfo) &&
      !isLessonLive(itemInfo) &&
      ((isLesson && isInCourse) || (isTest && isInTestSeries));

    const isNeoLesson = itemInfo.classType === NEOLESSON;

    const gotoMenuLabel = getGotoMenuLabel(itemInfo, isLesson, isSpecial);
    const goToCoursePage = () => {
      const course = itemInfo.programme;
      if (isSpecial && isInCourse) {
        pushRoute(`${itemInfo.relativeLink}?noAutoplay=true`);
      } else {
        const itemPrefix = isInCourse ? 'course' : 'test-series';
        pushRoute(
          `/${itemPrefix}/[slug]/[courseUID]`,
          `/${itemPrefix}/${course.slug}/${course.uid}`
        );
      }
      closeActionMenu();
    };

    const { mutate } = useSWRConfig();
    const entityType = isLesson
      ? savedObjectTypes.LESSON
      : savedObjectTypes.TEST;

    const handlePostSave = (res, isSave = false) => {
      const message = isSave
        ? `Saved ${isLesson ? 'Lesson' : 'Test'} successfully`
        : `Removed from Saved ${isLesson ? 'Lessons' : 'Tests'} successfully`;
      if (res?.successCode) {
        mutate(endpoint, { ...itemInfo, isBookmarked: isSave });
        showSnackbar({
          message,
          type: 'success'
        });
      } else {
        showSnackbar({
          message: BACKEND_API_ERROR,
          type: 'error'
        });
      }
    };

    const handleUnsaveItem = () => {
      if (itemInfo) {
        unsave(itemInfo.uid, entityType).then((res) => handlePostSave(res));
      }
      onCloseModal();
      setClickAwayActive(true);
    };

    const handleSaveItem = () => {
      save(itemInfo.uid, entityType).then((res) => handlePostSave(res, true));
    };

    const handleSaveOrUnsaveItem = () => {
      if (!itemInfo.isBookmarked) {
        closeActionMenu();
        handleSaveItem();
      } else {
        setClickAwayActive(false);
        setShowUnsavedDialog(true);
      }
    };

    const goToEducatorPage = () => {
      pushRoute('/[authorID]', `/@${author.username}`);
      closeActionMenu();
    };

    return (
      <>
        <Dialog
          onClose={onCloseModal}
          dialogState={showUnsaveDialog}
          heading="Remove from Saved?"
          message={`This will remove the ${
            isLesson ? 'lesson' : 'test'
          } from your saved list. You can add it back from the planner again.`}
          messageWidth="82.5%"
          acceptLabel="Remove"
          rejectLabel="Cancel"
          acceptOnClick={handleUnsaveItem}
          rejectOnClick={onCloseModal}
        />
        <div ref={ref}>
          {slides && (
            <>
              <PDFDownload
                slides={slides}
                handleClose={closeActionMenu}
                isNeoLesson={isNeoLesson}
              />
              <Divider $marginBottom={Spacings.SPACING_6B} />
            </>
          )}
          {enableBookmark && (
            <>
              <MenuItem onClick={handleSaveOrUnsaveItem}>
                {itemInfo.isBookmarked ? (
                  <>
                    <Saved size="20" color="var(--color-text-secondary)" />
                    <MenuItemLabel>Saved</MenuItemLabel>
                  </>
                ) : (
                  <>
                    <Save size="20" color="var(--color-text-secondary)" />
                    <MenuItemLabel>Save</MenuItemLabel>
                  </>
                )}
              </MenuItem>
            </>
          )}
          {author && !isOfflineCourseItem && (
            <LinkMenuItem onClick={goToEducatorPage} label="View educator" />
          )}
          {!isPlatformGoal && (
            <LinkMenuItem onClick={goToCoursePage} label={gotoMenuLabel} />
          )}
        </div>
      </>
    );
  }
);

PlannerMenuContent.displayName = 'PlannerMenuContent';

export default PlannerMenuContent;
